import { animated, useSpring } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconAnglesRight } from '#src/atoms/icons/system/iconAnglesRight.ts';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { BrandXXS } from '#src/atoms/typography/index.tsx';
import { glossary } from '#src/lib/glossary.ts';
import type { Color } from '#src/style/types.ts';

import flashBg from './assets/flash.webp';

const WIDTH = 280;

const Wrapper = styled.div<{ disabled: boolean }>`
  --main-color: var(--c-green-400);
  --size: calc(5 * var(--unit));
  --half-size: calc(0.5 * var(--size));
  position: relative;
  width: ${WIDTH}px;
  font-size: var(--t-16);
  font-weight: 700;
  font-family: inherit;
  isolation: isolate;
  box-sizing: border-box;
  border-radius: var(--half-size);
  background: var(--c-nd-50);
  border: 1px solid var(--main-color);
  backdrop-filter: blur(4px);
`;

const SlidingWindow = styled.div`
  overflow: hidden;
  border-radius: var(--half-size);
  user-select: none;
`;

const Message = styled(Horizontal).attrs({ gap: 0, center: true })`
  height: 100%;
  width: calc(100% - 8px);
  text-align: center;
  flex: 1;
`;

const Shadow = styled(animated.div)`
  position: absolute;
  inset: 0;
  z-index: -1;
  box-shadow: 0 0 var(--quadruple-unit) var(--half-unit) var(--main-color);
  border-radius: var(--half-size);
  opacity: 0;
`;

const Before = styled(animated.div)`
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  inset: 0;
  border-radius: var(--half-size);
  z-index: -1;
  background: var(--background-color, var(--c-black));
  &:after {
    content: '';
    background: var(--main-color);
    background-size: cover;
    position: absolute;
    inset: 0;
    mask-size: cover;
    mask-image: var(--background-image, url(${flashBg}));
  }
`;

const Slider = styled(animated.div)`
  height: 100%;
  display: flex;
  align-items: center;
`;

const SlidingButton = styled(animated.div)`
  display: flex;
  justify-content: center;
  align-items: center;
  touch-action: none;
  margin: 0;
  background: var(--c-white);
  box-shadow:
    1px 1px 2px 0px rgba(255, 255, 255, 0.3) inset,
    -1px -1px 2px 0px rgba(111, 111, 111, 0.5) inset;
  text-align: center;
  user-select: none;
  aspect-ratio: 1;
  width: var(--size);
  border-radius: 50%;
  color: var(--c-black);
  padding: 0;
  cursor: move;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 1);

  &:disabled {
    background: linear-gradient(0deg, #5a5858, #858585);
  }
  &.availableSoon {
    cursor: not-allowed;
  }
`;

type Props = {
  disabled?: boolean;
  onConfirm: () => Promise<void>;
  availableSoon?: boolean;
  onScroll?: (scroll: number) => void;
  ctaMessage?: React.ReactNode;
  color?: Color;
  style?: React.CSSProperties;
  disabledText?: ReactNode;
};
export const ConfirmSlider = ({
  disabled = false,
  onConfirm,
  onScroll,
  availableSoon,
  ctaMessage = <FormattedMessage {...glossary.confirm} />,
  color: colorProp = 'var(--c-green-400)',
  style,
  disabledText = <FormattedMessage defaultMessage="Not enough essence" />,
}: Props) => {
  const color = disabled ? 'var(--c-nd-600)' : colorProp;
  const { formatMessage } = useIntl();
  const triggered = useRef(false);
  const dragging = useRef(false);

  const [{ x }, api] = useSpring(() => ({
    from: {
      x: 0,
    },
  }));

  const bind = useDrag(async ({ event, active, down, movement: [mouseX] }) => {
    event.preventDefault();
    if (!disabled) {
      dragging.current = active;
      if (mouseX < WIDTH && mouseX >= 0) {
        onScroll?.(mouseX / WIDTH);
      }
      if (mouseX > WIDTH && !triggered.current && !active) {
        triggered.current = true;
        await onConfirm().finally(() => {
          triggered.current = false;
        });
      }
      const xValue = Math.min(
        // can't go too far
        WIDTH,
        Math.max(
          // can't go back once triggered, and prevent dragging left
          triggered.current ? WIDTH : 0,
          // mouse drag
          active ? mouseX : 0
        )
      );
      onScroll?.(xValue / WIDTH);
      api.start({
        x: xValue,
        immediate: down,
      });
    }
  });
  return (
    <Wrapper
      disabled={disabled}
      style={
        {
          '--main-color': color,
          ...style,
        } as CSSProperties
      }
    >
      <Shadow
        style={{
          opacity: x.to(t => t / WIDTH),
        }}
      />
      <Before
        style={{
          clipPath: x.to(t => `rect(0 ${t + 20}px 100% 0)`),
        }}
      />
      <SlidingWindow>
        <Slider style={{ x }}>
          <SlidingButton
            className={classNames({ availableSoon })}
            {...bind()}
            aria-label={formatMessage({
              defaultMessage: 'Slider',
            })}
          >
            <FontAwesomeIcon icon={iconAnglesRight} />
          </SlidingButton>
          <Message>
            <BrandXXS color={color}>
              {!!disabled && disabledText}
              {!disabled && <>{ctaMessage}</>}
            </BrandXXS>
          </Message>
        </Slider>
      </SlidingWindow>
    </Wrapper>
  );
};
